/**
 * Seating geometry for an AUTHENTIC D-shape (half-round) blackjack table.
 *
 * The table is the casino classic: a STRAIGHT (flat) edge at the back where the
 * croupier stands/sits, and a CURVED edge sweeping toward the players, who arc
 * around it. The flat edge faces away from the camera (toward the High Rollers
 * sign); the curve opens toward the camera so the player seats read in frame.
 *
 * This is the ONE source of truth: the felt outline, the painted betting-circle
 * markers, the blob positions, the card/chip anchors, and the croupier arc all
 * derive from the numbers here so they always agree.
 *
 * Coordinate convention (world units ≈ meters):
 *   +x = right, +z = toward the camera (near), -z = far (behind the dealer).
 *   The flat edge sits at z = `FLAT_Z` (negative). The curve bulges to +z.
 */

import * as THREE from 'three';

/**
 * Table footprint + heights. Enlarged ~1.4–1.5× from the first cut so the 11
 * betting boxes + the arc text have comfortable room (the felt is no longer a
 * cramped central strip). The depth was raised proportionally MORE than the width
 * so the player arc is a fuller fan, not a shallow near-flat band.
 */
export const TABLE = {
  /** Half-width of the D across the flat edge (x extent). Widened so the 11
   *  betting boxes spread out and clear the arc text. */
  radiusX: 4.3,
  /** Depth of the curved bulge measured from the flat edge toward the camera.
   *  Deepened too so the box row and the arc-text rows have a comfortable gap. */
  radiusZ: 3.2,
  /** Felt top height above the floor (rests on a pedestal at y=0 floor). */
  topY: 0.95,
  /** Tiny lift so painted markings/cards never z-fight the felt. */
  surfaceLift: 0.004,
} as const;

/**
 * The flat (dealer) edge z-coordinate. The D's straight side lives here; the
 * semicircular/elliptical curve sweeps from this line out to `FLAT_Z + radiusZ`.
 * Kept negative so the croupier is at the far edge and the curve opens to camera.
 */
export const FLAT_Z = -TABLE.radiusZ * 0.5;

/**
 * Dealer cutout (the rectangular notch cut INTO the table toward the players on
 * the flat edge — the dealer's standing recess that holds the chip tray + shoe).
 * The notch removes felt from the flat edge inward (+z) over the center span.
 */
export const NOTCH = {
  /** Half-width of the notch along x (centered on x=0). */
  halfW: 1.2,
  /** How far the notch cuts into the felt toward the players (+z). */
  depth: 0.62,
} as const;

/** Number of arced player betting spots around the curve (Fix 2: 11 players). */
export const PLAYER_SEAT_COUNT = 11;

/**
 * Arc margin (in the t parameter) kept clear of each flat-edge corner so the end
 * seats sit comfortably on the curve and their betting boxes stay inside the felt
 * with a clear margin. Shared by computeSeats (seating) and feltMarkings so the
 * painted boxes and the chip/card anchors line up.
 */
export const SEAT_MARGIN = 0.1;

/** Radius of a painted betting box (drawn into the felt canvas). */
export const BET_CIRCLE_R = 0.27;

/**
 * Layered rim widths (world units), inner→outer from the felt edge:
 *   felt | wood inner rail | padded leather bumper (studded) .
 * Used by both the geometry (Table.tsx) and to keep markings inside the felt.
 * Scaled up with the bigger table.
 */
export const RIM = {
  /** Wood inner rail width. */
  wood: 0.2,
  /** Padded leather bumper width (outermost). */
  bumper: 0.34,
} as const;

/**
 * Shared table silhouette as a list of [x, z] points (world XZ), traced
 * counter-clockwise from the left flat corner, along the flat dealer edge, then
 * the half-ellipse curve back to the left corner.
 *
 * `expand` offsets the outline outward (positive, for the wood rail + leather
 * bumper rings) or inward (negative, for the felt-margin used by markings).
 *
 * `notch` cuts the centered rectangular DEALER CUTOUT into the flat edge (toward
 * the players). Only the FELT layer uses the notch; the wood rail + outer bumper
 * run straight across the dealer edge (notch=false) — exactly like a real table,
 * where the padded armrest spans the dealer station and the felt recess + chip
 * tray sit inset from it. The notch shrinks with outward `expand` so a slightly
 * expanded felt-lip still shows the cutout.
 *
 * This is the SINGLE source of the table silhouette: Table.tsx builds felt/rail/
 * bumper from it and feltMarkings.ts insets its drawing region from it.
 */
export function feltOutline(expand: number, notch: boolean, segs = 72): Array<[number, number]> {
  const rx = TABLE.radiusX + expand;
  const rz = TABLE.radiusZ + expand;
  // Flat edge moves outward (more negative z) as we expand the rings outward.
  const flat = FLAT_Z - expand;

  const pts: Array<[number, number]> = [];
  pts.push([-rx, flat]); // left flat corner

  if (notch) {
    // Notch narrows + shallows as the outline expands outward (clamped ≥ 0).
    const nHalf = Math.max(0.05, NOTCH.halfW - expand);
    const nDepth = Math.max(0.05, NOTCH.depth - expand);
    const nBackZ = FLAT_Z + nDepth; // notch back wall (toward players)
    pts.push([-nHalf, flat]); // to left side of the notch
    pts.push([-nHalf, nBackZ]); // into the table (left wall)
    pts.push([nHalf, nBackZ]); // across the back of the notch
    pts.push([nHalf, flat]); // back out (right wall)
  }

  pts.push([rx, flat]); // right flat corner
  // Half-ellipse curve back to the left corner (theta 0 → PI). `segs` controls the
  // sampling density (higher = smoother gold-trim tube, fewer aliasing facets).
  for (let i = 1; i <= segs; i++) {
    const theta = (i / segs) * Math.PI;
    pts.push([Math.cos(theta) * rx, FLAT_Z + Math.sin(theta) * rz]);
  }
  return pts;
}

export interface Seat {
  /** Index 0..n-1 along the curved (player) edge, left→right. */
  readonly index: number;
  /** Parametric position 0..1 of this seat along the arc (for felt markings). */
  readonly t: number;
  /** Angle (radians) of this seat on the table ellipse, measured from +x. */
  readonly angle: number;
  /** World position of the seat's chair / blob center (on the floor, y=0). */
  readonly floor: THREE.Vector3;
  /** World position where this seat's painted betting circle sits on the felt. */
  readonly betCircle: THREE.Vector3;
  /** World position where this seat's cards lay on the felt (just inside the circle). */
  readonly cardAnchor: THREE.Vector3;
  /** World position for this seat's chip stack (on the betting circle). */
  readonly chipAnchor: THREE.Vector3;
  /** Y-rotation so the blob faces the table center. */
  readonly facing: number;
}

/**
 * A point on the felt's CURVED player edge at parameter `t` (0..1): t=0 = left
 * flat corner, t=1 = right flat corner, t=0.5 = front-center (max +z). This is
 * the SAME half-ellipse the table's `feltOutline()` traces for its curved edge,
 * centered on the flat-edge midpoint `(0, FLAT_Z)` and bulging toward the players
 * (+z). Returns world `[x, z]`.
 */
export function playerEdgePoint(t: number): [number, number] {
  const theta = Math.PI * (1 - t);
  return [Math.cos(theta) * TABLE.radiusX, FLAT_Z + Math.sin(theta) * TABLE.radiusZ];
}

/**
 * A point offset INWARD from the felt's curved player edge by a constant WORLD
 * distance `inward` (meters), along the ellipse's inward normal. This is the fix
 * for the "inverted D" bug: a *concentric, constant-distance* offset of the
 * player curve nests correctly INSIDE it (same concavity, bulging toward the
 * players). The previous radial-scale `(1-inset)` collapsed the arc toward the
 * dealer-edge midpoint, so inset arcs pulled toward the DEALER and the end boxes
 * jammed against the flat edge — the curvature looked mirrored.
 *
 * Negative `inward` offsets OUTWARD (used for the blob/chair ring outside the rim).
 *
 * Both `computeSeats` (anchors) and `feltMarkings` (painted boxes/text) call this
 * so the drawing and the geometry can never diverge.
 */
export function playerArcPoint(t: number, inward: number): [number, number] {
  const theta = Math.PI * (1 - t);
  const x = Math.cos(theta) * TABLE.radiusX;
  const z = FLAT_Z + Math.sin(theta) * TABLE.radiusZ;
  // Outward (unnormalized) normal of the ellipse (x/rx)^2 + ((z-FLAT_Z)/rz)^2 = 1
  // is gradient (2x/rx^2, 2(z-FLAT_Z)/rz^2); the inward normal is its negative.
  let nx = Math.cos(theta) / TABLE.radiusX;
  let nz = Math.sin(theta) / TABLE.radiusZ;
  const len = Math.hypot(nx, nz) || 1;
  nx /= len;
  nz /= len;
  // Move INWARD (toward the felt interior) by `inward`, i.e. opposite the normal.
  return [x - nx * inward, z - nz * inward];
}

/**
 * Inward offsets (WORLD meters) from the player edge for each marking/anchor row,
 * shared with feltMarkings so the painted boxes/ticks coincide with the anchors.
 * Larger = deeper toward the dealer/center.
 */
export const ARC_INWARD = {
  /** Betting box + chip anchor row — kept SHALLOW (near the player edge) so there
   *  is a comfortable gap to the arc-text rows behind it. */
  box: 0.58,
  /** Card anchor row — sits just behind the box, BETWEEN the box and the text
   *  rows. (The visible white "card-slot" tick is no longer painted; this is only
   *  where the dealt cards land.) */
  card: 0.95,
  /** Blob/chair ring (negative = OUTSIDE the rim). */
  blob: -1.05,
} as const;

/**
 * Compute `count` evenly-spaced player seats around the curved edge of the D.
 * Defaults to {@link PLAYER_SEAT_COUNT} (11). Seats are biased away from the very
 * ends of the flat edge so they never overlap the croupier's corners.
 */
export function computeSeats(count: number = PLAYER_SEAT_COUNT): Seat[] {
  const seats: Seat[] = [];
  if (count <= 0) return seats;

  // Keep a margin in from each flat-edge corner so end seats sit on the curve.
  const margin = SEAT_MARGIN;
  const span = 1 - margin * 2;

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : margin + (i / (count - 1)) * span;

    // Concentric inward offsets of the player edge (the fix). Blob/chair sits
    // OUTSIDE the rim; betting box + cards sit progressively INSIDE it. These
    // MUST match feltMarkings.ts (same playerArcPoint + ARC_INWARD) so the
    // chips/cards land exactly on the painted betting box + card-slot tick.
    const [fx, fz] = playerArcPoint(t, ARC_INWARD.blob);
    const [bx, bz] = playerArcPoint(t, ARC_INWARD.box);
    const [cx, cz] = playerArcPoint(t, ARC_INWARD.card);

    const floor = new THREE.Vector3(fx, 0, fz);
    const betCircle = new THREE.Vector3(bx, TABLE.topY + TABLE.surfaceLift, bz);
    const cardAnchor = new THREE.Vector3(cx, TABLE.topY + TABLE.surfaceLift, cz);
    // Chips sit slightly toward the player from the betting box center.
    const [chx, chz] = playerArcPoint(t, ARC_INWARD.box - 0.08);
    const chipAnchor = new THREE.Vector3(chx, TABLE.topY + TABLE.surfaceLift, chz);
    // Face the table center (the dealer arc), looking inward.
    const facing = Math.atan2(-floor.x, FLAT_Z + TABLE.radiusZ * 0.35 - floor.z);

    seats.push({ index: i, t, angle: Math.PI * (1 - t), floor, betCircle, cardAnchor, chipAnchor, facing });
  }
  return seats;
}

/**
 * Map `playerCount` players onto a CENTERED subset of the 11 fixed painted boxes.
 *
 * The 11 betting boxes are painted at fixed positions on the felt (feltMarkings
 * uses PLAYER_SEAT_COUNT), so players must occupy a subset of those exact spots —
 * otherwise their chips/cards wouldn't land on a painted box. Filling 0..N-1 left-
 * clustered the players at one end (the "trouble for more than one person"); this
 * centers them instead, so e.g. 1 player sits at the middle box, 2 straddle the
 * center, and 11 fill every box. Returns player-index → fixed-seat-index (0..10).
 *
 * ALL consumers (Blobs, Chips, cardLayout) must use this so blobs/cards/chips for
 * a given player share one seat.
 */
export function assignSeatIndices(playerCount: number): number[] {
  const n = Math.max(0, Math.min(playerCount, PLAYER_SEAT_COUNT));
  const start = Math.floor((PLAYER_SEAT_COUNT - n) / 2);
  return Array.from({ length: n }, (_, i) => start + i);
}

/**
 * The croupier/house position at the flat (straight) far edge. Seat 1 of 12.
 * The dealer stands IN the notch; the chip tray + shoe sit inside the cutout, and
 * the dealer's cards lay on the felt just past the notch back wall (so they don't
 * land in the empty cutout), centered, facing the players.
 */
export const CROUPIER = {
  floor: new THREE.Vector3(0, 0, FLAT_Z - 0.95),
  betCircle: new THREE.Vector3(0, TABLE.topY + TABLE.surfaceLift, FLAT_Z + NOTCH.depth + 0.28),
  cardAnchor: new THREE.Vector3(0, TABLE.topY + TABLE.surfaceLift, FLAT_Z + NOTCH.depth + 0.28),
  chipAnchor: new THREE.Vector3(0, TABLE.topY + TABLE.surfaceLift, FLAT_Z + NOTCH.depth + 0.12),
  /** Faces the players (toward +z / camera). */
  facing: Math.PI,
} as const;

/** Center of the felt (community dice land here). */
export const TABLE_CENTER = new THREE.Vector3(
  0,
  TABLE.topY + TABLE.surfaceLift,
  FLAT_Z + TABLE.radiusZ * 0.5,
);
